import { FormEvent, KeyboardEvent, useEffect, useRef, useState } from "react";
import { QuizCards } from "./QuizCards";
import styles from "./styles.module.css";
import type {
  QuizCard,
  QuizChatResponse,
  QuizDetail,
  QuizNextAction,
} from "../../store/api/quizChatApi";
import {
  useChatQuizRetrievalMutation,
  useGetQuizRetrievalDetailLazyQuery,
  useSearchQuizRetrievalMutation,
} from "../../store/api/quizChatApi";

type FocusTarget = "detail" | "results" | null;

const MESSAGE_ROLE = { USER: "user", ASSISTANT: "assistant" } as const;
type MessageRole = (typeof MESSAGE_ROLE)[keyof typeof MESSAGE_ROLE];

interface ChatMessage {
  id: number;
  role: MessageRole;
  text: string;
  hasResults?: boolean;
}

const errorMessage = (error: unknown) => {
  if (typeof error === "string") return error;
  if (error && typeof error === "object" && "data" in error) {
    const data = (error as { data?: unknown }).data;
    return typeof data === "string"
      ? data
      : "No fue posible recuperar los quizzes.";
  }
  return "No fue posible recuperar los quizzes.";
};

const unique = (values: string[]) => Array.from(new Set(values));

const responseWarnings = (
  cards: QuizCard[],
  details: QuizDetail[],
  warnings: string[],
) =>
  unique([
    ...warnings,
    ...cards.flatMap((card) => card.warnings),
    ...details.flatMap((detail) => [
      ...detail.warnings,
      ...detail.media.flatMap((media) =>
        media.diagnostic ? [media.diagnostic] : [],
      ),
    ]),
  ]);

export default function QuizRetrievalChat() {
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null);
  const detailHeadingRef = useRef<HTMLHeadingElement>(null);
  const requestVersion = useRef(0);
  const [prompt, setPrompt] = useState("");
  const [cards, setCards] = useState<QuizCard[]>([]);
  const [detailIds, setDetailIds] = useState<number[]>([]);
  const [selectedDetail, setSelectedDetail] = useState<QuizDetail | null>(null);
  const [next, setNext] = useState<QuizNextAction | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [status, setStatus] = useState("Listo para buscar quizzes.");
  const [error, setError] = useState("");
  const [focusTarget, setFocusTarget] = useState<FocusTarget>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [messageId, setMessageId] = useState(0);
  const [chatQuizRetrieval, { isLoading: isChatLoading }] =
    useChatQuizRetrievalMutation();
  const [searchQuizRetrieval, { isLoading: isSearchLoading }] =
    useSearchQuizRetrievalMutation();
  const [getDetail, { isFetching: isDetailLoading }] =
    useGetQuizRetrievalDetailLazyQuery();

  useEffect(() => {
    if (focusTarget === "results") resultsHeadingRef.current?.focus();
    if (focusTarget === "detail") detailHeadingRef.current?.focus();
    if (focusTarget) setFocusTarget(null);
  }, [focusTarget, cards, selectedDetail]);

  const acceptChat = (response: QuizChatResponse) => {
    setCards(response.cards);
    setDetailIds(response.actions.details);
    setSelectedDetail(response.details[0] ?? null);
    setNext(response.actions.next);
    setWarnings(responseWarnings(response.cards, response.details, response.warnings));
    setStatus(
      response.actions.next
        ? "Resultados canónicos cargados. Hay más resultados disponibles."
        : "Resultados canónicos cargados. Esta es la página final.",
    );
    setFocusTarget("results");
  };

  const addMessage = (role: MessageRole, text: string, hasResults = false) => {
    setMessageId((current) => {
      setMessages((currentMessages) => [
        ...currentMessages,
        { id: current + 1, role, text, hasResults },
      ]);
      return current + 1;
    });
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const version = ++requestVersion.current;
    setError("");
    const submittedPrompt = prompt.trim();
    if (!submittedPrompt) return;
    addMessage(MESSAGE_ROLE.USER, submittedPrompt);
    setPrompt("");
    try {
      const response = await chatQuizRetrieval({ prompt: submittedPrompt }).unwrap();
      if (version === requestVersion.current) {
        acceptChat(response);
        addMessage(MESSAGE_ROLE.ASSISTANT, response.model_text ?? "Encontré estos quizzes.", true);
      }
    } catch (requestError) {
      if (version === requestVersion.current) {
        setError(errorMessage(requestError));
        setStatus("La búsqueda falló.");
        addMessage(MESSAGE_ROLE.ASSISTANT, "No pude recuperar los quizzes. Revisá la solicitud e intentá nuevamente.");
        inputRef.current?.focus();
      }
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== "Enter" || event.shiftKey) return;
    void handleSubmit(event);
  };

  const handleNext = async () => {
    if (!next) return;
    const version = ++requestVersion.current;
    setError("");
    try {
      const response = await searchQuizRetrieval(next).unwrap();
      if (version !== requestVersion.current) return;
      setCards(response.items);
      setSelectedDetail(null);
      setNext(
        response.actions?.next ??
          (response.next_cursor
            ? { cursor: response.next_cursor, filters: next.filters }
            : null),
      );
      setWarnings(unique([...response.warnings, ...response.items.flatMap((item) => item.warnings)]));
      setStatus(
        response.terminal
          ? "Se alcanzó la página final."
          : "Se cargaron los siguientes resultados canónicos.",
      );
      setFocusTarget("results");
      addMessage(
        MESSAGE_ROLE.ASSISTANT,
        response.terminal ? "Estos son los últimos resultados." : "Encontré más quizzes para revisar.",
        true,
      );
    } catch (requestError) {
      if (version === requestVersion.current) {
        setError(errorMessage(requestError));
        setStatus("La siguiente página no pudo recuperarse.");
        addMessage(MESSAGE_ROLE.ASSISTANT, "No pude cargar la siguiente página de resultados.");
      }
    }
  };

  const handleDetail = async (id: number) => {
    const version = ++requestVersion.current;
    setError("");
    try {
      const response = await getDetail(id).unwrap();
      if (version === requestVersion.current) {
        setSelectedDetail(response);
        setWarnings(
          unique([
            ...warnings,
            ...response.warnings,
            ...response.media.flatMap((media) =>
              media.diagnostic ? [media.diagnostic] : [],
            ),
          ]),
        );
        setStatus(`Se cargó el detalle canónico del quiz ${id}.`);
        setFocusTarget("detail");
      }
    } catch (requestError) {
      if (version === requestVersion.current) {
        setError(errorMessage(requestError));
        setStatus("El detalle del quiz no pudo recuperarse.");
      }
    }
  };

  const isLoading = isChatLoading || isSearchLoading || isDetailLoading;
  const latestResultsMessageId = messages.reduce<number | null>(
    (latest, message) => (message.hasResults ? message.id : latest),
    null,
  );

  return (
    <main className={styles.chatPage}>
      <header className={styles.chatHeader}>
        <div>
          <p className={styles.eyebrow}>MCP · Solo lectura</p>
          <h1>Búsqueda de quizzes</h1>
          <p>Consultá quizzes de Emmi para revisión. Podés combinar trimestre, grado, materia, semana y lección.</p>
        </div>
      </header>
      <section className={styles.transcript} aria-label="Conversación">
        {messages.length === 0 ? (
          <div className={styles.emptyState}>
            <h2>¿Qué quizzes querés revisar?</h2>
            <p>Escribí una consulta en lenguaje natural. El asistente devuelve resultados estructurados y no modifica contenido.</p>
            <ul>
              <li>“Obtené todos los quizzes de T1 de Matemáticas de 3°.”</li>
              <li>“Dame los quizzes de Ciencias de 4° de la semana 1 a la 6.”</li>
              <li>“Dame el quiz 123.”</li>
            </ul>
          </div>
        ) : messages.map((message) => (
          <article key={message.id} className={`${styles.message} ${styles[message.role]}`} aria-label={message.role === MESSAGE_ROLE.USER ? "Tu solicitud" : "Respuesta del asistente"}>
            <p className={styles.messageRole}>{message.role === MESSAGE_ROLE.USER ? "Vos" : "Asistente"}</p>
            <p>{message.text}</p>
            {message.hasResults && message.id === latestResultsMessageId && (
              <div className={styles.resultsInMessage}>
                <QuizCards
                  cards={cards}
                  detail={selectedDetail}
                  detailIds={detailIds}
                  onDetail={handleDetail}
                  resultsHeadingRef={resultsHeadingRef}
                  detailHeadingRef={detailHeadingRef}
                />
                <button type="button" onClick={handleNext} disabled={!next || isLoading}>
                  Siguientes resultados
                </button>
              </div>
            )}
          </article>
        ))}
      </section>
      <form className={styles.composer} onSubmit={handleSubmit}>
        <label htmlFor="quiz-retrieval-request">Solicitud de búsqueda</label>
        <textarea
          id="quiz-retrieval-request"
          ref={inputRef}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ej.: Obtené todos los quizzes de T1 de Matemáticas de 3°…"
          rows={3}
          required
        />
        <div className={styles.composerFooter}>
          <span>Enter para enviar · Shift + Enter para una nueva línea</span>
          <button type="submit" aria-label="Buscar quizzes" disabled={isLoading || !prompt.trim()}>Enviar solicitud</button>
        </div>
      </form>
      <div role="status" aria-live="polite">
        <p>{status}</p>
        {warnings.map((warning) => <p key={warning}>{warning}</p>)}
      </div>
      <p role="alert" aria-live="assertive">{error}</p>
      {isLoading && <p className={styles.loading} role="status">El asistente está buscando quizzes…</p>}
    </main>
  );
}
